if (!customElements.get('card-custom-picker')) {
  customElements.define(
    'card-custom-picker',
    class CardCustomPicker extends HTMLElement {
      connectedCallback() {
        this.dataEl = this.querySelector('[data-card-picker-data]');
        if (!this.dataEl) return;

        try {
          this.data = JSON.parse(this.dataEl.textContent);
        } catch (e) {
          console.error('card-custom-picker: invalid JSON', e);
          return;
        }

        this.image = this.querySelector('[data-card-primary-image]');
        this.quickAddBtn = this.querySelector('[data-card-quick-add]');
        this.sizesEl = this.querySelector('[data-card-sizes]');
        this.sizeButtons = Array.from(this.querySelectorAll('[data-card-size]'));
        this.colorButtons = Array.from(this.querySelectorAll('[data-card-color]'));
        this.variantInput = this.querySelector('[data-card-variant-id]');
        this.submitButton = this.querySelector('.card-custom-picker__submit');
        this.priceEl = this.querySelector('.price');

        this.selectedColor = this.getInitialColor();
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);

        this.colorButtons.forEach((btn) => {
          btn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.selectColor(btn.dataset.color);
          });
        });

        this.quickAddBtn?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.onQuickAddClick();
        });

        this.sizeButtons.forEach((btn) => {
          btn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.onSizeClick(btn);
          });
        });

        this.updateForColor(this.selectedColor, { updateImage: false });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeyDown);
      }

      getInitialColor() {
        const selected = this.colorButtons.find((btn) => btn.classList.contains('is-selected'));
        if (selected) return selected.dataset.color;
        if (this.colorButtons[0]) return this.colorButtons[0].dataset.color;

        const { colorOptionIndex, variants } = this.data;
        if (colorOptionIndex == null || !variants?.length) return null;
        return variants[0].options[colorOptionIndex] || null;
      }

      selectColor(color) {
        this.selectedColor = color;
        this.colorButtons.forEach((btn) => {
          const isSelected = btn.dataset.color === color;
          btn.classList.toggle('is-selected', isSelected);
          btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });
        this.closeSizes();
        this.updateForColor(color, { updateImage: true });
      }

      updateForColor(color, { updateImage = true } = {}) {
        const { colorOptionIndex, sizeOptionIndex, hasSizeOption, variants } = this.data;
        const matching = variants.filter((variant) => {
          if (colorOptionIndex == null || color == null) return true;
          return variant.options[colorOptionIndex] === color;
        });

        const availableMatch = matching.find((variant) => variant.available) || matching[0];
        if (availableMatch) {
          this.setVariantId(availableMatch.id, availableMatch.available);
          if (updateImage && availableMatch.featuredImage) {
            this.updateImage(availableMatch);
          }
          if (availableMatch.price) {
            this.updatePrice(availableMatch);
          }
        }

        if (hasSizeOption && sizeOptionIndex != null) {
          this.sizeButtons.forEach((btn) => {
            const size = btn.dataset.size;
            const variant = matching.find((item) => item.options[sizeOptionIndex] === size);
            if (!variant) {
              btn.disabled = true;
              btn.classList.add('is-unavailable');
              btn.removeAttribute('data-variant-id');
              return;
            }
            btn.dataset.variantId = String(variant.id);
            btn.disabled = !variant.available;
            btn.classList.toggle('is-unavailable', !variant.available);
          });
        }

        if (this.quickAddBtn) {
          const anyAvailable = matching.some((variant) => variant.available);
          this.quickAddBtn.disabled = !anyAvailable;
        }
      }

      updateImage(variant) {
        if (!this.image) return;
        if (variant.srcset) this.image.srcset = variant.srcset;
        if (variant.featuredImage) this.image.src = variant.featuredImage;
      }

      updatePrice(variant) {
        if (!this.priceEl) return;
        const regular = this.priceEl.querySelector('.price-item--regular');
        const sale = this.priceEl.querySelector('.price-item--sale');
        if (sale && variant.compareAtPrice) {
          sale.textContent = variant.price;
          if (regular) regular.textContent = variant.compareAtPrice;
        } else if (regular) {
          regular.textContent = variant.price;
        } else if (sale) {
          sale.textContent = variant.price;
        }
      }

      onQuickAddClick() {
        if (!this.data.hasSizeOption || !this.sizesEl) {
          this.addCurrentVariant();
          return;
        }
        this.openSizes();
      }

      openSizes() {
        this.classList.add('is-sizes-open');
        this.sizesEl.hidden = false;
        this.quickAddBtn.hidden = true;
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeyDown);
      }

      closeSizes() {
        if (!this.sizesEl) return;
        this.classList.remove('is-sizes-open');
        this.sizesEl.hidden = true;
        if (this.quickAddBtn) this.quickAddBtn.hidden = false;
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeyDown);
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) {
          this.closeSizes();
        }
      }

      onKeyDown(event) {
        if (event.key === 'Escape') this.closeSizes();
      }

      onSizeClick(btn) {
        if (btn.disabled || !btn.dataset.variantId) return;
        this.setVariantId(btn.dataset.variantId, true);
        this.addCurrentVariant();
        this.closeSizes();
      }

      setVariantId(id, available) {
        if (!this.variantInput) return;
        this.variantInput.value = id;
        this.variantInput.disabled = !available;
        if (this.submitButton) {
          if (available) {
            this.submitButton.removeAttribute('disabled');
          } else {
            this.submitButton.setAttribute('disabled', 'disabled');
          }
        }
      }

      addCurrentVariant() {
        if (!this.submitButton || this.submitButton.disabled) return;
        this.submitButton.click();
      }
    }
  );
}
